import React from "react";
import { MessageCircle, Search, Send } from "lucide-react";

/**
 * Jobs-to-be-done section: what the visitor is trying to accomplish, rather
 * than a list of features.
 *
 * COPY RULES BAKED INTO THIS FILE (do not "improve" them into promises):
 * every line describes an action the platform makes possible - browsing,
 * reaching out, continuing a conversation. Nothing here promises sobriety,
 * relapse prevention, successful recovery, better treatment outcomes or
 * repaired relationships, because Sponsor Me cannot deliver or measure any of
 * those. "May", "can" and "if they accept" are deliberate.
 */
const BENEFITS = [
  {
    icon: Search,
    title: "Find someone who gets it",
    body: "Learn about a sponsor's publicly shared experience and approach before reaching out.",
  },
  {
    icon: Send,
    title: "Take the next step",
    body: "Request a connection instead of waiting until you happen to meet the right person.",
  },
  {
    icon: MessageCircle,
    title: "Build real connection",
    body: "If the sponsor accepts, continue the conversation through Sponsor Me's messaging system.",
  },
];

export default function ActiveStep() {
  return (
    <section
      data-testid="home-active-step"
      aria-labelledby="active-step-heading"
      className="bg-white border-y border-[#E3DED4]"
    >
      <div className="max-w-7xl mx-auto px-4 py-14 lg:py-16">
        <div className="max-w-3xl">
          <h2
            id="active-step-heading"
            data-testid="active-step-heading"
            className="font-display text-2xl sm:text-3xl font-bold text-[#1F2A2E]"
          >
            Take an Active Step in Your Recovery
          </h2>
          <p data-testid="active-step-intro" className="mt-3 text-[#4B5A60] leading-8">
            Finding the right person to talk to can be difficult. Sponsor Me gives you a private way
            to explore potential peer sponsors, understand their approach, and take the first step
            toward building a real recovery connection.
          </p>
        </div>

        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {BENEFITS.map((b) => (
            <article
              key={b.title}
              data-testid={`active-step-card-${b.title.split(" ")[0].toLowerCase()}`}
              className="sm-card p-6 bg-[#FBF9F5] flex flex-col gap-3"
            >
              <span className="w-10 h-10 rounded-xl bg-[#D7EEF0] text-[#1F6F78] flex items-center justify-center">
                <b.icon className="w-5 h-5" aria-hidden="true" />
              </span>
              <h3 className="font-display text-lg font-bold text-[#1F2A2E]">{b.title}</h3>
              <p className="text-sm text-[#4B5A60] leading-6">{b.body}</p>
            </article>
          ))}
        </div>

        <p data-testid="active-step-boundary" className="mt-8 text-sm text-[#4B5A60] leading-7 max-w-3xl">
          Sponsor Me helps people connect with volunteer peer sponsors. It is not therapy, medical
          care, professional counseling, addiction treatment, or emergency services. Sponsorship here
          is peer support - one person with lived experience talking with another.
        </p>
      </div>
    </section>
  );
}
